import { CloseDealNegotiationDocument } from '@/lib/graphql/generated/graphql';
import { trackEvent } from '@/lib/mixpanel';
import { isAbortError } from '@/lib/utils/is-abort-error';
import { dealRefetchAtom } from '@/states/deal.state';
import { useMutation } from '@apollo/client/react';
import { useAtomValue } from 'jotai';

export function useCloseNegotiation(dealId: string) {
  const dealRefetch = useAtomValue(dealRefetchAtom);
  const [closeDealNegotiationMutation, { loading, error }] = useMutation(
    CloseDealNegotiationDocument
  );

  const closeNegotiation = async () => {
    trackEvent('DEAL_NEGOTIATION_CLOSE_STARTED', { dealId });
    try {
      const result = await closeDealNegotiationMutation({
        variables: { dealId },
      });
      const response = result.data?.closeDealNegotiation;
      if (response?.__typename === 'DomainException') {
        trackEvent('DEAL_NEGOTIATION_CLOSE_FAILED', {
          dealId,
          errorCode: response.code,
        });
      } else {
        trackEvent('DEAL_NEGOTIATION_CLOSE_SUCCEEDED', { dealId });
      }
      // Best-effort UI refresh: the mutation already succeeded, so a refetch
      // that gets aborted/superseded must not turn a successful close into a
      // thrown error (which would surface a false close-negotiation failure).
      // A genuine refetch failure (network/query error) is still logged so it
      // is not silently hidden.
      await dealRefetch.fn().catch((refetchError) => {
        if (!isAbortError(refetchError)) {
          console.error(
            'Failed to refetch deal after closing negotiation:',
            refetchError
          );
        }
      });
      return result;
    } catch (err) {
      trackEvent('DEAL_NEGOTIATION_CLOSE_FAILED', {
        dealId,
        errorCode: err instanceof Error ? err.message : 'unknown_error',
      });
      throw err;
    }
  };

  return {
    closeNegotiation,
    loading,
    error,
  };
}
